import { lazy } from 'react'
import type { Routes } from '@/@types/routes'

/**
 * Password reset and email confirmation are real now.
 *
 * They were absent until Firebase landed, and the comment here used to explain
 * that they were left out rather than routed to a form with no backend behind
 * it. Firebase generates the links, `apps/accounts` mails them through our own
 * relay, and `/auth/action` is where they come back to.
 *
 * `/auth/action` is one route for every Firebase email action, dispatching on
 * `?mode=`. Firebase can send a mode we do not expose, and one route means that
 * redirects to sign-in rather than rendering nothing on a link the user was
 * told to trust.
 */
const authRoute: Routes = [
    {
        key: 'signIn',
        path: `/sign-in`,
        component: lazy(() => import('@/views/auth/SignIn')),
        authority: [],
    },
    {
        key: 'signUp',
        path: `/sign-up`,
        component: lazy(() => import('@/views/auth/SignUp')),
        authority: [],
    },
    {
        key: 'forgotPassword',
        path: `/forgot-password`,
        component: lazy(() => import('@/views/auth/ForgotPassword')),
        authority: [],
    },
    {
        // Where a sign-in lands when the address has not been confirmed. Not an
        // error page — the backend deliberately issues no session and creates
        // no account until somebody proves they own the mailbox.
        key: 'verifyEmail',
        path: `/verify-email`,
        component: lazy(() => import('@/views/auth/VerifyEmail')),
        authority: [],
    },
    {
        // Where a sign-in lands while the account waits for approval (the
        // private beta). Not an error page either: the account exists, and the
        // backend withholds only the session until an operator lets them in.
        key: 'waitlist',
        path: `/waitlist`,
        component: lazy(() => import('@/views/auth/Waitlist')),
        authority: [],
    },
    {
        // The second step of a sign-in for an account with two-factor on. The
        // first step worked and the backend issued a challenge instead of a
        // session; this page trades it and a code for the session. Public, like
        // the two above: nobody is signed in yet.
        key: 'twoFactor',
        path: `/two-factor`,
        component: lazy(() => import('@/views/auth/TwoFactor')),
        authority: [],
    },
    {
        key: 'authAction',
        path: `/auth/action`,
        component: lazy(() => import('@/views/auth/ActionHandler')),
        authority: [],
    },
]

export default authRoute
